import { createContext, useContext, type ReactNode } from 'react'

import type { DataGridColumnSchema, DataGridFacet, DataGridFilter } from '../lib/types'
import { DataGridContext } from './data-grid-context'

export type FilterFacetState = {
  search: string
  isLoading: boolean
  error: Error | null
  facet?: DataGridFacet
}

/**
 * What the filter controls read and change. A grid supplies its own; anything else that filters,
 * such as a dashboard, supplies one through `FilterSourceProvider` and gets the same controls.
 */
export type FilterSource = {
  columns: readonly DataGridColumnSchema[]
  filters: readonly DataGridFilter[] | undefined
  setFilters: (filters: readonly DataGridFilter[]) => void
  facets: Partial<Record<string, DataGridFacet>>
  facetState: Partial<Record<string, FilterFacetState>>
  loadFacetOptions: (column: string, search?: string, append?: boolean) => void | Promise<void>
}

const FilterSourceContext = createContext<FilterSource | null>(null)

export function FilterSourceProvider({ source, children }: { source: FilterSource; children: ReactNode }) {
  return <FilterSourceContext value={source}>{children}</FilterSourceContext>
}

export function useFilterSource(): FilterSource {
  const source = useContext(FilterSourceContext)
  const gridContext = useContext(DataGridContext)
  if (source) return source
  if (!gridContext) throw new Error('Filter controls require DataGrid.Root or FilterSourceProvider')
  const { grid } = gridContext
  return {
    columns: grid.schema.columns,
    filters: grid.criteria.filter,
    setFilters: grid.setFilters as FilterSource['setFilters'],
    facets: grid.facets,
    facetState: grid.facetState,
    loadFacetOptions: grid.loadFacetOptions as FilterSource['loadFacetOptions'],
  }
}
